/**
 * 法规知识库选择器的「纯逻辑核」—— 不依赖 JSON import，可被 frontend / Node 脚本 / 测试复用。
 *
 * 关系：
 * - complianceKnowledge.js     生产入口，import 静态 JSON，调本文件的 createKnowledge(kbData)
 * - scripts/demo-selector.mjs  Node demo，用 fs 读 JSON，调本文件
 * - __tests__/                 测试通过 complianceKnowledge.js 间接验证本文件
 *
 * 保持本文件零外部依赖、纯函数 / 不可变。
 */

const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };

function toLowerSet(arr) {
  if (!Array.isArray(arr)) return new Set();
  return new Set(
    arr
      .filter(x => typeof x === 'string' && x.trim())
      .map(x => x.toLowerCase().trim())
  );
}

function normalizeFacts(projectFacts = {}) {
  return {
    categories: toLowerSet(projectFacts.categories),
    features: toLowerSet(projectFacts.features),
    chains: toLowerSet(projectFacts.chains),
    target_markets: toLowerSet(projectFacts.target_markets),
    keywords: toLowerSet(projectFacts.keywords),
  };
}

/** 单 topic 对一组（已归一化的）project facts 的命中判定 */
export function topicMatches(topic, normalizedFacts) {
  const rules = Array.isArray(topic.applicable_when) ? topic.applicable_when : [];
  for (const rule of rules) {
    if (!rule || typeof rule !== 'object') continue;
    for (const field of ['categories', 'features', 'chains', 'target_markets']) {
      const ruleValues = toLowerSet(rule[field]);
      const factSet = normalizedFacts[field];
      for (const v of ruleValues) {
        if (factSet.has(v)) return { matched: true, reason: `${field}:${v}` };
      }
    }
  }

  const topicKws = toLowerSet(topic.keywords);
  for (const fact of normalizedFacts.keywords) {
    for (const kw of topicKws) {
      if (kw.includes(fact) || fact.includes(kw)) {
        return { matched: true, reason: `keyword:${kw}` };
      }
    }
  }

  return { matched: false, reason: null };
}

/**
 * 工厂：给定一份 kbData（来自 build-compliance-index.mjs 生成的 JSON），返回知识库 API。
 *
 * @param {{topics: object[], jurisdictions: string[], index: object, generated_at: string}} kbData
 */
export function createKnowledge(kbData) {
  if (!kbData || !Array.isArray(kbData.topics)) {
    throw new Error('createKnowledge: invalid kbData (missing topics array)');
  }

  function loadAllTopics() {
    return kbData.topics;
  }

  function getKbMetadata() {
    return {
      generatedAt: kbData.generated_at,
      jurisdictions: kbData.jurisdictions,
      index: kbData.index,
      topicCount: kbData.topics.length,
    };
  }

  function getJurisdictionTopics(jurisdiction) {
    return kbData.topics.filter(
      t => t.jurisdiction === jurisdiction && t.topic !== '_index'
    );
  }

  function getTopic(jurisdiction, topic) {
    return kbData.topics.find(
      t => t.jurisdiction === jurisdiction && t.topic === topic
    ) || null;
  }

  function selectRelevantTopics(projectFacts, options = {}) {
    const { onlyJurisdictions, includeIndex = false } = options;
    const facts = normalizeFacts(projectFacts);
    const jurisdictionFilter = Array.isArray(onlyJurisdictions) && onlyJurisdictions.length
      ? new Set(onlyJurisdictions.map(j => j.toLowerCase()))
      : null;

    const hits = [];
    for (const topic of kbData.topics) {
      if (jurisdictionFilter && !jurisdictionFilter.has(topic.jurisdiction)) continue;
      if (!includeIndex && topic.topic === '_index') continue;
      const match = topicMatches(topic, facts);
      if (match.matched) {
        hits.push({ topic, matchReason: match.reason });
      }
    }

    hits.sort((a, b) => {
      const sa = SEVERITY_RANK[a.topic.severity_if_breached] || 0;
      const sb = SEVERITY_RANK[b.topic.severity_if_breached] || 0;
      if (sb !== sa) return sb - sa;
      if (a.topic.jurisdiction !== b.topic.jurisdiction) {
        return a.topic.jurisdiction.localeCompare(b.topic.jurisdiction);
      }
      return a.topic.topic.localeCompare(b.topic.topic);
    });

    return hits;
  }

  return {
    loadAllTopics,
    getKbMetadata,
    getJurisdictionTopics,
    getTopic,
    selectRelevantTopics,
  };
}
